import classNames from 'classnames';
import { inflect } from 'inflection';
import * as _ from 'lodash-es';
import React, { useState } from 'react';
import Badge from '../../components/Badge';
import SVGIcon from '../../components/SVGIcon';
import Tooltip from '../../components/Tooltip';
import FormField from '../../components/formInternals/FormField';
import type { DocumentType } from '../../graphql/generated/gql';
import { gql } from '../../graphql/generated/gql';
import type {
  BillingSummaryStatus,
  PlanInterval,
  PlanTier,
} from '../../graphql/generated/graphql';
import informationIcon from '../../icons/non-prefilled/16x16/information.svg';
import useSeatLimits from '../../invitation/useSeatLimits';
import { GuestsPerSeat } from '../../organization/PricingConfig';
import formatCurrency from '../../utils/formatCurrency';
import Link from '../../utils/router/Link';
import * as styles from './PlanRow.module.scss';
import { getTotalForInterval } from './priceCalculator';

const INTERVAL_TEXT = {
  YEARLY: 'year',
  MONTHLY: 'month',
  QUARTERLY: 'quarter',
};

const getSeatsText = ({
  minimumUserLimit,
  seatCount,
  userLimit,
  amount,
}: {
  minimumUserLimit: number | null;
  seatCount: number;
  userLimit: number | null;
  amount: number | null;
}) => {
  if (minimumUserLimit) {
    return `${minimumUserLimit} seats minimum`;
  }

  // TODO: fix later
  return amount === 0
    ? // @ts-expect-error
      // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
      `${Math.min(seatCount, userLimit)} of ${userLimit} users`
    : `${seatCount} ${inflect('seat', seatCount)} × ${formatCurrency(
        amount ?? 0,
      )}`;
};

const BILLING_TIER_FRAGMENT = gql(`
  fragment PlanRow_billingTier on BillingTier {
    monthly {
      id
      amount
    }
    yearly {
      id
      amount
    }
  }
`);

const BILLING_COUPON_FRAGMENT = gql(`
  fragment PlanRow_billingCoupon on BillingCoupon {
    ...getTotalForInterval_billingCoupon
  }
`);

interface PlanRowProps {
  status?: BillingSummaryStatus;
  billingCoupon?: DocumentType<typeof BILLING_COUPON_FRAGMENT> | null;
  interval: PlanInterval;
  onSelect?: () => void;
  billingTier?: DocumentType<typeof BILLING_TIER_FRAGMENT>;
  recommended?: boolean;
  selected?: boolean;
  tooltip: string;
  planTier: PlanTier;
  userLimit?: number | null;
  minimumUserLimit?: number | null;
}

const PlanRow = ({
  status,
  billingCoupon,
  interval,
  onSelect,
  billingTier,
  recommended,
  selected = false,
  tooltip,
  planTier,
  userLimit = null,
  minimumUserLimit = null,
}: PlanRowProps) => {
  const { seatCount } = useSeatLimits({
    guestsPerSeat: GuestsPerSeat[planTier],
  });

  const intervalText = INTERVAL_TEXT[interval];

  const [isHovering, setIsHovering] = useState(false);
  const isSeatLimitMaxed = _.isNumber(userLimit) && seatCount > userLimit;
  const isDisabledWithTooltip = status === 'TRIALING' || isSeatLimitMaxed;

  const isDisabled = isDisabledWithTooltip || !billingTier;
  const amount = billingTier
    ? (billingTier[interval.toLowerCase() as 'monthly' | 'yearly']?.amount ??
      null)
    : null;

  const totalCost = !_.isNil(amount)
    ? getTotalForInterval(amount, seatCount, billingCoupon)
    : null;

  let disabledTooltip;

  if (isDisabledWithTooltip && isHovering) {
    disabledTooltip = isSeatLimitMaxed
      ? `Your team must have ${userLimit} users or fewer to downgrade`
      : 'Your team will automatically be moved to Free after your trial ends';
  }

  const handleHover = (value: boolean) => {
    if (isDisabledWithTooltip) {
      setIsHovering(value);
    }
  };

  return (
    <label
      className={classNames(styles.row, {
        [styles.active]: selected && !isDisabled,
        [styles.disabled]: isDisabled,
      })}
      onMouseEnter={() => handleHover(true)}
      onMouseLeave={() => handleHover(false)}
    >
      <Tooltip trigger="clickOrHover" content={disabledTooltip}>
        <FormField
          type="radio"
          name="tier"
          value={planTier}
          margin="none"
          disabled={isDisabled}
          onChange={(e) => {
            if (e.target.checked && onSelect) {
              onSelect();
            }
          }}
          checked={selected}
        />
      </Tooltip>
      <div className={styles.nameColumn}>
        <strong className={styles.name}>{_.capitalize(planTier)}</strong>
        <Tooltip
          trigger="clickOrHover"
          content={disabledTooltip ? undefined : tooltip}
          display="block"
        >
          <SVGIcon className={styles.infoIcon}>{informationIcon}</SVGIcon>
        </Tooltip>
        {recommended && !selected && (
          <div className={styles.recommendedBadgeContainer}>
            <Badge color="yellow">Recommended</Badge>
          </div>
        )}
      </div>
      <div className={styles.unit}>
        {getSeatsText({
          minimumUserLimit,
          seatCount,
          userLimit,
          amount,
        })}
      </div>
      <div className={styles.price}>
        {billingTier ? (
          <>
            {totalCost}/{intervalText}
          </>
        ) : (
          <Link to="https://go.slab.com/contact-sales" newTab>
            Contact us
          </Link>
        )}
      </div>
    </label>
  );
};

export default PlanRow;
